/**
 * Типы клиентских событий. Канон — shotbook-backend/config/tracking_events.php:
 * неизвестный тип приём отклоняет, поэтому новые события заводятся там до релиза (TRK-8).
 */
export type AppEventType =
  // Навигация и здоровье клиента
  | 'page_view'
  | 'cta_click'
  | 'js_error'
  | 'web_vital'
  | 'api_error'
  // Аутентификация
  | 'auth_screen_shown'
  | 'login_attempt'
  | 'login_success'
  | 'login_failed'
  | 'register_attempt'
  | 'register_success'
  | 'register_failed'
  | 'google_oauth_failed'
  | 'password_reset_requested'
  | 'password_reset_confirmed'
  | 'password_reset_failed'
  | 'email_verification_success'
  | 'email_verification_failed'
  | 'email_verification_resend'
  | 'logout'
  // Поиск и каталог
  | 'search_query'
  | 'search_result_clicked'
  | 'search_zero_results_shown'
  | 'infinite_scroll_load_more'
  | 'filter_change'
  | 'filters_cleared'
  // Видео
  | 'video_open'
  | 'video_detail_closed'
  | 'video_detail_navigate'
  | 'video_external_click'
  | 'video_link_copied'
  | 'tutorial_link_clicked'
  | 'hosted_playback_started'
  | 'hosted_playback_error'
  // Подписка
  | 'subscribe_click'
  | 'paywall_shown'
  | 'subscription_modal_open'
  | 'subscription_modal_close'
  | 'subscribe_blocked_no_paddle'
  | 'subscribe_blocked_no_user'
  | 'paddle_sdk_load_start'
  | 'paddle_sdk_load_success'
  | 'paddle_sdk_load_failed'
  | 'paddle_config_request'
  | 'paddle_config_success'
  | 'paddle_config_failed'
  | 'checkout_params_request'
  | 'checkout_params_success'
  | 'checkout_params_failed_conflict'
  | 'checkout_params_failed'
  | 'paddle_checkout_open'
  | 'paddle_checkout_loaded'
  | 'paddle_checkout_warning'
  | 'paddle_checkout_error'
  | 'paddle_checkout_closed'
  | 'paddle_checkout_completed'
  | 'paddle_payment_selected'
  | 'paddle_payment_initiated'
  | 'paddle_payment_failed'
  | 'subscription_polling_start'
  | 'subscription_polling_success'
  | 'subscription_polling_timeout'
  | 'resume_request'
  | 'resume_success'
  | 'resume_failed'
  | 'change_plan_request'
  | 'change_plan_success'
  | 'change_plan_failed'
  | 'cancel_request'
  | 'cancel_success'
  | 'cancel_failed'
  | 'portal_request'
  | 'portal_success'
  | 'portal_failed'
  // Баннеры, помощь, запрос референса
  | 'banner_view'
  | 'banner_dismiss'
  | 'help_opened'
  | 'help_closed'
  | 'empty_results_reference_request_banner_shown'
  | 'empty_results_reference_request_banner_dismissed'
  | 'empty_results_reference_request_banner_opened'
  | 'welcome_reference_request_prompt_shown'
  | 'welcome_reference_request_prompt_closed'
  | 'welcome_reference_request_prompt_opened'
  // Публичный канвас (имена сохраняются ради непрерывности рядов, TRK-3)
  | 'shared_board_opened'
  | 'shared_board_cta_click'
  // Канвас (CNV-28). TODO(B8): зарегистрировать в config/tracking_events.php до релиза.
  | 'canvas_created'
  | 'canvas_opened'
  | 'canvas_renamed'
  | 'canvas_deleted'
  | 'canvas_set_default'
  | 'canvas_node_added'
  | 'canvas_frame_created'
  | 'canvas_inout_set'
  | 'canvas_connection_created'
  | 'canvas_shared'
  | 'canvas_share_link_copied'
  | 'canvas_share_revoked'
  | 'canvas_participant_joined'
  | 'reference_saved_to_canvas'
  | 'reference_removed_from_canvas'
  // Онбординг (ONB-7). TODO(B8): зарегистрировать в config/tracking_events.php до релиза.
  | 'onboarding_shown'
  | 'onboarding_step_viewed'
  | 'onboarding_next'
  | 'onboarding_back'
  | 'onboarding_skipped'
  | 'onboarding_completed'
  | 'onboarding_search_focused';

/**
 * События воронки уходят сразу одиночным POST — их нельзя терять и задерживать.
 * Остальное копится в батч.
 */
const IMMEDIATE: ReadonlySet<AppEventType> = new Set<AppEventType>([
  'auth_screen_shown',
  'login_attempt',
  'login_success',
  'login_failed',
  'register_attempt',
  'register_success',
  'register_failed',
  'google_oauth_failed',
  'email_verification_success',
  'email_verification_failed',
  'email_verification_resend',
  'password_reset_requested',
  'password_reset_confirmed',
  'password_reset_failed',
  'logout',
  'subscribe_click',
  'paywall_shown',
  'search_result_clicked',
  'shared_board_cta_click',
  'video_external_click',
  'tutorial_link_clicked',
  'video_link_copied',
  'reference_saved_to_canvas',
  'onboarding_completed',
  'onboarding_skipped',
]);

export function isImmediateEvent(type: AppEventType): boolean {
  return (
    IMMEDIATE.has(type) ||
    type.startsWith('paddle_') ||
    type.startsWith('checkout_') ||
    type.startsWith('subscription_') ||
    type.startsWith('subscribe_') ||
    type.startsWith('resume_') ||
    type.startsWith('change_plan_') ||
    type.startsWith('cancel_') ||
    type.startsWith('portal_')
  );
}

/** Платёжная воронка: в бесплатном режиме не отправляется (BILL-8, D-A-007). */
export function isPaymentEvent(type: AppEventType): boolean {
  return (
    type === 'paywall_shown' ||
    type.startsWith('subscribe_') ||
    type.startsWith('subscription_') ||
    type.startsWith('paddle_') ||
    type.startsWith('checkout_') ||
    type.startsWith('resume_') ||
    type.startsWith('change_plan_') ||
    type.startsWith('cancel_') ||
    type.startsWith('portal_')
  );
}
